import openaiService from './openaiService';

/**
 * AI Insights Service
 * Shared helper functions for generating mindset summaries and reflection prompts
 * Used by Self Dashboard for growth-focused AI insights
 * 
 * Includes localStorage caching (24h expiry) to avoid re-calling AI on every load
 */

const CACHE_EXPIRY_HOURS = 24;
const CACHE_KEYS = {
  MINDSET: 'self_ai_mindset',
  REFLECTION: 'self_ai_reflection',
  SWIRL_SUMMARY: 'swirl_summary',
  DEEPER_QUESTIONS: 'self_deeper_questions'
};

/**
 * Clear all AI insights cache (for debugging/fixing incorrect summaries)
 */
export function clearAICache() {
  try {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ AI insights cache cleared');
  } catch (error) {
    console.warn('Error clearing cache:', error);
  }
}

// Expose to window for debugging
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.clearAICache = clearAICache;
}

/**
 * Get cached value if still valid
 * @param {string} key - Cache key
 * @returns {Object|null} - Cached data with timestamp, or null if expired/missing
 */
function getCached(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    const now = Date.now();
    const age = now - parsed.timestamp;
    const expiryMs = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    
    if (age > expiryMs) {
      localStorage.removeItem(key);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.warn('Cache read error:', error);
    return null;
  }
}

/**
 * Store value in cache with timestamp
 * @param {string} key - Cache key
 * @param {string} value - Value to cache
 */
function setCached(key, value) {
  try {
    const data = {
      value,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Cache write error:', error);
  }
}

/**
 * Generate a mindset summary based on recent content items
 * Uses caching (24h expiry) to avoid unnecessary AI calls
 * @param {Array} contentItems - Array of user's recent content items
 * @returns {Promise<string>} - Formatted insight string
 */
export async function generateMindsetSummary(contentItems = []) {
  try {
    if (!contentItems || contentItems.length === 0) {
      return "You're just getting started — your learning journey is beginning to take shape.";
    }

    // TEMPORARILY DISABLE CACHE to fix incorrect summaries
    // TODO: Re-enable cache after verifying data is correct
    // const cached = getCached(CACHE_KEYS.MINDSET);
    // if (cached) {
    //   console.log('✅ Using cached AI mindset summary');
    //   return cached.value;
    // }

    // Filter out test/example content and validate items
    const validItems = contentItems.filter(item => {
      // Filter out obvious test content
      const title = (item.title || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      const testKeywords = ['test', 'qa', 'example', 'sample', 'demo', 'placeholder', 'lorem'];
      
      // Skip if title/description contains test keywords
      const isTestContent = testKeywords.some(keyword => 
        title.includes(keyword) || desc.includes(keyword)
      );
      
      // Skip if no real title/description
      const hasRealContent = item.title && item.title.length > 3;
      
      return !isTestContent && hasRealContent;
    });

    if (validItems.length === 0) {
      console.warn('⚠️ No valid items found after filtering test content');
      return "You're just getting started — your learning journey is beginning to take shape.";
    }

    // Debug: Log what items are being used
    if (import.meta.env.DEV) {
      console.log('🔍 generateMindsetSummary: Using items:', {
        totalItems: contentItems.length,
        validItems: validItems.length,
        sampleItems: validItems.slice(0, 3).map(item => ({
          id: item.id,
          title: item.title?.slice(0, 40),
          tags: item.auto_tags?.topics || [],
          source_type: item.source_type,
        })),
      });
    }

    // Prepare content summary for AI - use only valid items
    const inputText = validItems.slice(0, 10).map((item) => {
      const title = item.title || item.description || '';
      const desc = item.description || '';
      const tags = item.auto_tags?.topics?.slice(0, 3).join(', ') || '';
      return `${title}${desc ? `: ${desc}` : ''}${tags ? ` [${tags}]` : ''}`;
    }).join('\n');

    const prompt = `Summarize what this user has been learning or exploring lately from their saved content.
Describe the mindset or growth pattern behind their recent activity.
Tone: calm, intelligent, curious.
Avoid over-explaining - 2-3 sentences max.
Sound like observing the user, not teaching them.

IMPORTANT: At the end of your response, identify their emotional or cognitive tone in ONE word from this exact list: [Curious, Driven, Reflective, Calm, Focused]. Format it as: "Tone: [word]"

User's recent content:
${inputText}`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a thoughtful observer of personal growth and learning patterns. Provide gentle, insightful observations.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 100
    });

    let result;
    if (response.error || !response.data) {
      console.warn('AI insight generation failed, using fallback');
      result = "You're exploring new ideas and building your understanding — keep following your curiosity.";
    } else {
      result = response.data.trim();
      // Cache the result
      setCached(CACHE_KEYS.MINDSET, result);
    }

    return result;
  } catch (error) {
    console.error('Error generating mindset summary:', error);
    return "You're exploring new ideas and building your understanding — keep following your curiosity.";
  }
}

/**
 * Extract tone label from mindset summary text
 * @param {string} summaryText - The mindset summary text
 * @returns {string} - Tone label (Curious, Driven, Reflective, Calm, Focused, or null)
 */
/**
 * Generate deeper questions/curiosities from themes
 * Uses caching (24h expiry) to avoid unnecessary AI calls
 * @param {Array<string>} themes - Array of theme names
 * @param {Array} items - Array of content items for context
 * @returns {Promise<Array<string>>} - Array of 3-4 deeper questions
 */
export async function generateDeeperQuestions(themes = [], items = []) {
  try {
    if (!themes || themes.length === 0) {
      return [];
    }

    // Check cache first
    const cacheKey = `${CACHE_KEYS.DEEPER_QUESTIONS}_${themes.slice(0, 5).join('_')}`;
    const cached = getCached(cacheKey);
    if (cached) {
      if (import.meta.env.DEV) {
        console.log('✅ Using cached deeper questions');
      }
      return cached.value;
    }

    // Prepare context from items
    const itemContext = items.slice(0, 5).map((item, idx) => {
      const title = item.title || '';
      const desc = item.description || '';
      return `${idx + 1}. "${title}"${desc ? `: ${desc.substring(0, 80)}` : ''}`;
    }).join('\n');

    const prompt = `Generate 3-4 deeper questions or curiosities that connect these themes: ${themes.join(', ')}.

These questions should:
- Be specific and insightful (not generic)
- Reveal the underlying curiosity or driving question
- Connect multiple themes when possible
- Sound like genuine questions someone exploring these themes would ask

${itemContext ? `Context from user's recent content:\n${itemContext}` : ''}

Output ONLY a JSON array of 3-4 questions, like: ["question 1?", "question 2?", "question 3?"]`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a thoughtful observer who identifies deeper questions and curiosities. Always respond with a valid JSON array of 3-4 questions.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 200
    });

    let questions = [];
    if (response.error || !response.data) {
      console.warn('AI question generation failed, using fallback');
      // Fallback: generate simple questions from themes
      questions = themes.slice(0, 3).map(theme => `What insights can I gain from exploring ${theme}?`);
    } else {
      try {
        const rawData = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
        const jsonMatch = rawData.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          questions = JSON.parse(jsonMatch[0]);
        } else {
          // Fallback parsing
          questions = themes.slice(0, 3).map(theme => `What insights can I gain from exploring ${theme}?`);
        }
      } catch (parseError) {
        console.warn('Error parsing questions JSON:', parseError);
        questions = themes.slice(0, 3).map(theme => `What insights can I gain from exploring ${theme}?`);
      }
    }

    // Cache the result
    setCached(cacheKey, questions);

    return questions;
  } catch (error) {
    console.error('Error generating deeper questions:', error);
    return [];
  }
}

export function extractToneFromSummary(summaryText) {
  if (!summaryText) return null;
  
  const toneMatch = summaryText.match(/Tone:\s*([A-Za-z]+)/i);
  if (toneMatch) {
    const tone = toneMatch[1];
    const validTones = ['Curious', 'Driven', 'Reflective', 'Calm', 'Focused'];
    if (validTones.includes(tone)) {
      return tone;
    }
  }
  
  return null;
}

/**
 * Generate a personalized reflection prompt based on themes and current mood
 * Phase 7.1: Enhanced with mood awareness for Reflection Loop
 * @param {Array<string>} themes - Array of recurring themes/topics
 * @param {string} currentMood - Optional current mood (Curious, Driven, Reflective, Calm, Focused)
 * @returns {Promise<string>} - Formatted reflection prompt string
 */
export async function generateReflectionPrompt(themes = [], currentMood = null) {
  try {
    const themesText = themes.length > 0 
      ? themes.slice(0, 5).join(', ')
      : 'various topics';

    const moodContext = currentMood 
      ? ` Your current state feels ${currentMood.toLowerCase()}.`
      : '';

    // Check cache first (use themes + mood for cache key variation)
    const cacheKey = `${CACHE_KEYS.REFLECTION}.${themesText}.${currentMood || 'none'}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('✅ Using cached AI reflection prompt');
      return cached.value;
    }

    const prompt = `Based on these recent exploration themes: ${themesText}${moodContext}
Generate a personalized weekly reflection prompt.
Make it feel like gentle self-inquiry.
Example: "You've been exploring [theme] and [theme] lately — what resonated most this week?"
Keep it concise (1-2 sentences max).
Tone: calm, intelligent, curious.`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a thoughtful guide for personal reflection. Create gentle, inviting prompts for self-inquiry.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 80
    });

    let result;
    if (response.error || !response.data) {
      console.warn('AI reflection prompt generation failed, using fallback');
      result = `You've been exploring ${themesText} lately — want to journal about what drives you?`;
    } else {
      result = response.data.trim();
      // Cache the result with specific key
      setCached(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error('Error generating reflection prompt:', error);
    const themesText = themes.length > 0 ? themes.slice(0, 2).join(' and ') : 'new ideas';
    return `You've been exploring ${themesText} lately — want to journal about what drives you?`;
  }
}

/**
 * Generate image summary with OCR text and visual tags
 * Phase 10: Smart Image Ingestion
 * LOCAL DEVELOPMENT ONLY - Do not deploy to production
 * 
 * @param {Object} params - Image analysis parameters
 * @param {string} params.text - Extracted OCR text from image
 * @param {string[]} params.visualTags - Visual tags from GPT-4 Vision analysis
 * @returns {Promise<{summary: string, bullet_points: string[], tone: string, themes: string[], tags: string[]}>}
 */
export async function generateImageSummary({ text, visualTags = [] }) {
  try {
    const systemPrompt = `You are Swirl's visual comprehension AI. Summarize this image context and text into structured output.

Analyze the image content and extracted text to provide:
- 1-line summary of the image
- 3-5 bullet points highlighting key themes
- Dominant mood or tone (e.g., motivational, analytical, reflective, educational)
- Suggested tags for discovery
- Merge both visual and textual understanding

Return a JSON object with this exact structure:
{
  "summary": "one-line summary",
  "bullet_points": ["point 1", "point 2", "point 3"],
  "tone": "descriptive tone",
  "themes": ["theme1", "theme2", "theme3"],
  "tags": ["tag1", "tag2", "tag3"]
}`;

    const userContent = JSON.stringify({
      text: text || '',
      visualTags: visualTags || []
    });

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: `Analyze this image content:\n\n${userContent}`
      }
    ], {
      model: 'gpt-4-turbo',
      temperature: 0.7,
      max_tokens: 1000
    });

    if (response.error || !response.data) {
      throw new Error(response.error || 'Failed to generate image summary');
    }

    // Parse JSON response
    let result;
    try {
      // Try to extract JSON from response
      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        result = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      // Fallback: create structured response from text
      const lines = response.data.split('\n').filter(l => l.trim());
      result = {
        summary: lines[0] || 'Image content analyzed',
        bullet_points: lines.slice(1, 6).filter(l => l.trim()),
        tone: 'Informative',
        themes: extractThemes(response.data),
        tags: [...visualTags, ...extractTags(response.data)]
      };
    }

    // Ensure all required fields exist
    return {
      summary: result.summary || 'Image content analyzed',
      bullet_points: result.bullet_points || [],
      tone: result.tone || 'Informative',
      themes: result.themes || [],
      tags: result.tags || []
    };

  } catch (error) {
    console.error('Error generating image summary:', error);
    // Return fallback structure
    return {
      summary: 'Image content analyzed',
      bullet_points: [],
      tone: 'Informative',
      themes: [],
      tags: visualTags || []
    };
  }
}

/**
 * Generate document summary from extracted text
 * Phase 10.1: Document & File Ingestion Engine
 * LOCAL DEVELOPMENT ONLY - Do not deploy to production
 * 
 * @param {Object} params - Document analysis parameters
 * @param {string} params.text - Extracted text from document
 * @returns {Promise<{summary: string, bullet_points: string[], tone: string, themes: string[], tags: string[]}>}
 */
export async function generateDocumentSummary({ text }) {
  try {
    if (!text || text.length < 50) {
      return {
        summary: 'Document content extracted',
        bullet_points: [],
        tone: 'Informative',
        themes: [],
        tags: []
      };
    }

    const systemPrompt = `You are Swirl's document summarizer. Summarize and analyze the uploaded content.

Summarize and analyze the document content. Provide:
- summary: concise 1-line overview
- bullet_points: 4–6 key takeaways
- tone: descriptive tone (e.g., motivational, analytical, reflective, educational)
- themes: 3–5 recurring ideas
- tags: discovery-friendly tags

Return a JSON object with this exact structure:
{
  "summary": "one-line summary",
  "bullet_points": ["point 1", "point 2", "point 3", "point 4"],
  "tone": "descriptive tone",
  "themes": ["theme1", "theme2", "theme3"],
  "tags": ["tag1", "tag2", "tag3"]
}`;

    // Truncate text to safe length (12000 chars) to avoid token limits
    const truncatedText = text.length > 12000 ? text.substring(0, 12000) + '...' : text;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: `Analyze this document content:\n\n${truncatedText}`
      }
    ], {
      model: 'gpt-4-turbo',
      temperature: 0.7,
      max_tokens: 1000
    });

    if (response.error || !response.data) {
      throw new Error(response.error || 'Failed to generate document summary');
    }

    // Parse JSON response
    let result;
    try {
      // Try to extract JSON from response
      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        result = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      // Fallback: create structured response from text
      const lines = response.data.split('\n').filter(l => l.trim());
      result = {
        summary: lines[0] || 'Document content analyzed',
        bullet_points: lines.slice(1, 7).filter(l => l.trim()),
        tone: 'Informative',
        themes: extractThemes(response.data),
        tags: extractTags(response.data)
      };
    }

    // Ensure all required fields exist
    return {
      summary: result.summary || 'Document content analyzed',
      bullet_points: result.bullet_points || [],
      tone: result.tone || 'Informative',
      themes: result.themes || [],
      tags: result.tags || []
    };

  } catch (error) {
    console.error('Error generating document summary:', error);
    // Return fallback structure
    return {
      summary: 'Document content analyzed',
      bullet_points: [],
      tone: 'Informative',
      themes: [],
      tags: []
    };
  }
}

/**
 * Extract themes from AI response text
 * @param {string} text - AI response text
 * @returns {string[]} Array of themes
 */
function extractThemes(text) {
  if (!text) return [];
  
  // Look for patterns like "themes:", "topics:", etc.
  const themePatterns = [
    /(?:themes?|topics?):\s*([^\n]+)/gi,
    /(?:theme|topic):\s*([^\n,]+)/gi
  ];
  
  const themes = [];
  for (const pattern of themePatterns) {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      const themeList = match[1].split(/[,;]/).map(t => t.trim()).filter(t => t);
      themes.push(...themeList);
    }
  }
  
  return [...new Set(themes)].slice(0, 5); // Max 5 themes, remove duplicates
}

/**
 * Extract tags from AI response text
 * @param {string} text - AI response text
 * @returns {string[]} Array of tags
 */
function extractTags(text) {
  if (!text) return [];
  
  // Look for patterns like "tags:", "suggested tags:", etc.
  const tagPatterns = [
    /(?:tags?|suggested tags?):\s*([^\n]+)/gi,
    /(?:tag):\s*([^\n,]+)/gi
  ];
  
  const tags = [];
  for (const pattern of tagPatterns) {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      const tagList = match[1].split(/[,;]/).map(t => t.trim()).filter(t => t);
      tags.push(...tagList);
    }
  }
  
  return [...new Set(tags)].slice(0, 10); // Max 10 tags, remove duplicates
}

/**
 * Generate AI summary for a Smart Swirl's evolution
 * Phase 8.2: Analyzes how a cluster evolved over time
 * @param {string} swirlId - Smart Swirl ID
 * @param {Array} evolutionData - Array of weekly evolution data
 * @returns {Promise<string>} - AI-generated summary text
 */
export async function generateSwirlSummary(swirlId, evolutionData = []) {
  try {
    // Check cache first
    const cacheKey = `${CACHE_KEYS.SWIRL_SUMMARY}.${swirlId}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('✅ Using cached Swirl summary');
      return cached.value;
    }

    if (!evolutionData || evolutionData.length === 0) {
      return "This cluster is just beginning to take shape — your exploration is evolving.";
    }

    // Prepare evolution summary for AI
    const evolutionText = evolutionData.map((week, index) => {
      const weekDate = new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const tags = (week.dominant_tags || []).slice(0, 3).join(', ') || 'various themes';
      return `Week ${index + 1} (${weekDate}): ${week.new_items} new items, ${Math.round(week.avg_similarity * 100)}% similarity, themes: ${tags}`;
    }).join('\n');

    const prompt = `Analyze how this cluster evolved over time.

Evolution data:
${evolutionText}

Summarize this cluster's evolution in a short paragraph (2-3 sentences).
Describe:
1. How the user's focus or curiosity shifted over time
2. What emotional or intellectual pattern this represents
Tone: calm, intelligent, observant. Sound like a thoughtful reflection on growth.

Example: "Your curiosity in this space has evolved beautifully — you started exploring productivity themes, which gradually deepened into creative efficiency as you discovered new connections."`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a thoughtful observer of personal growth and learning patterns. Provide gentle, insightful observations about how curiosity evolves.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 150
    });

    let result;
    if (response.error || !response.data) {
      console.warn('AI Swirl summary generation failed, using fallback');
      result = "Your curiosity in this space has evolved beautifully — themes and connections are deepening over time.";
    } else {
      result = response.data.trim();
      // Cache the result
      setCached(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error('Error generating Swirl summary:', error);
    return "Your curiosity in this space has evolved beautifully — themes and connections are deepening over time.";
  }
}

